import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
	Alert,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Container,
	Dialog,
	DialogContent,
	DialogTitle,
	Link,
	List,
	ListItem,
	ListItemAvatar,
	ListItemSecondaryAction,
	ListItemText,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from "@mui/material";
import {green} from "@mui/material/colors";
import {TemporalFormat, TemporalRangeFormat, ZonedDateTime} from "@variocube/app-ui";
import React, {useMemo, useState} from "react";
import {Accessibility} from "../../component/accessibility";
import {useBooking} from "../../context/booking";
import type {BookingBoxes} from "../../data/bookings";
import {B2Booking, B2BookingPayment} from "../../data/client.gen";
import {getZoneDateTimeNow, parseZoneDateTimeFromInstant} from "../../dates-utils";
import {useLocalization} from "../../i18n";
import {DisplayCurrency, notBlank} from "../../utils";
import {BookingHeader} from "./booking-header";
import {BookingOccupancy} from "./booking-occupancy";
import {
	BookingCancelHint,
	BookingEndedAlert,
	BookingExpirationCountDown,
	BookingNotStartedAlert,
	BookingProlongationButton,
	PendingPaymentButton,
	useProlongationOffered,
} from "./booking-panels";
import {OpenAdmissionButton} from "./open-admission-button";

export function BookingDetails() {
	const {t} = useLocalization();
	const {booking, bookingFrom, isBookingActive, refresh} = useBooking();
	const zoneId = booking?.site.zoneId ?? "UTC";
	// Evaluated once here and passed down (to the ended alert and the running-booking nudge) so the reveal timer and
	// its window-open refetch run a single time per view rather than once per consumer.
	const prolongationOffered = useProlongationOffered(booking, refresh);

	/**
	 * Pending booking is any bookings that has:
	 * - amount > 0
	 * - not yet paid
	 * - state equals to Pending
	 *
	 * This type of booking will be automatically canceled after a certain amount of time.
	 * Use this flag to toggle the warning alert on the UI.
	 */
	const isPendingBooking = useMemo(
		() => notBlank(booking?.paymentId) && booking?.state === "Pending" && booking.paymentStatus !== "paid",
		[booking],
	);

	/**
	 * Pending payment is any bookings that:
	 * - is not in pending state
	 * - has payment status as open
	 */
	const isPendingPayment = useMemo(() => !isPendingBooking && booking?.paymentStatus === "open", [
		isPendingBooking,
		booking,
	]);

	/**
	 * Canceled booking is any bookings that has:
	 * - inactive state
	 * - state equals to Canceled
	 *
	 * Use this flag to toggle the booking was canceled hint.
	 */
	const isBookingCanceled = useMemo(() => !isBookingActive && booking?.state === "Canceled", [
		isBookingActive,
		booking,
	]);

	/**
	 * Booking with active boxes has:
	 * - active state
	 * - payment status equals to paid
	 *
	 * Use this flag to toggle access to booking boxes.
	 */
	const isBoxesActive = useMemo(() => booking !== undefined && isBookingActive && booking.paymentStatus === "paid", [
		isBookingActive,
		booking,
	]);

	/**
	 * Booking that has not started yet: paid (or free) but the timeframe lies in the future. The locker card cannot be
	 * shown yet, so an informational alert takes its place.
	 */
	const isBookingNotStarted = useMemo(
		() => bookingFrom !== undefined && ZonedDateTime.compare(bookingFrom, getZoneDateTimeNow(zoneId)) > 0,
		[bookingFrom, zoneId],
	);

	return (
		<Container maxWidth="md">
			{booking !== undefined && (
				<Stack direction="column" gap={2} mt={2}>
					{
						/* Status alerts — when the locker card cannot be shown (unpaid, canceled, not started, ended),
					    an appropriate alert takes its place. */
					}
					{isPendingBooking && <BookingExpirationCountDown booking={booking} />}
					{isPendingPayment && <PendingPaymentButton booking={booking} />}
					{isBookingCanceled && <BookingCancelHint />}
					{!isBoxesActive && !isPendingBooking && !isPendingPayment && !isBookingCanceled && (
						isBookingNotStarted
							? <BookingNotStartedAlert booking={booking} />
							: <BookingEndedAlert booking={booking} prolongationOffered={prolongationOffered} />
					)}

					{/* The locker card — only for a paid and active booking. */}
					{isBoxesActive && <BookingOccupancy />}

					{/* Prolongation nudge for a running booking — the ended state offers it in BookingEndedAlert. */}
					{isBoxesActive && prolongationOffered && <BookingProlongationButton booking={booking} />}
					<OpenAdmissionButton booking={booking} />

					<Card>
						<CardHeader
							title={<BookingHeader />}
						/>
						<TableContainer component={CardContent}>
							<Table>
								<TableBody>
									<BookingRows booking={booking} />
									<TableRow>
										<TableCell component="th" scope="row">
											{t("dates.period")}:
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2">
												<TemporalRangeFormat
													from={parseZoneDateTimeFromInstant(booking.from, zoneId)}
													until={parseZoneDateTimeFromInstant(booking.until, zoneId)}
												/>
											</Typography>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell component="th" scope="row">
											{t("boxes.title")}:
										</TableCell>
										<TableCell align="right">
											{Object.entries(booking.boxes as BookingBoxes)
												.map(([size, quantity]) => (
													<div key={size}>{quantity} x {t("boxes.type")} {size}</div>
												))}
										</TableCell>
									</TableRow>
									{booking.payments !== undefined && booking.payments.length > 0 && (
										<TableRow>
											<TableCell component="th" scope="row" colSpan={2}>
												{t("payment.title")}
												<Box my={1} />

												<BookingPaymentList
													payments={booking.payments}
													currency={booking.site.currency}
													zoneId={zoneId}
												/>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Card>
				</Stack>
			)}
			<Box my={3} />
			{notBlank(booking?.site.terms) && (
				<Typography textAlign="center" gutterBottom>
					<Link href={booking?.site.terms} target="_blank">{t("sites.terms")}</Link>
				</Typography>
			)}
			{notBlank(booking?.site.privacyPolicy) && (
				<Typography textAlign="center" gutterBottom>
					<Link href={booking?.site.privacyPolicy} target="_blank">{t("sites.privacyPolicy")}</Link>
				</Typography>
			)}
		</Container>
	);
}

function BookingRows({booking}: { booking: B2Booking }) {
	const {t} = useLocalization();
	const {site, paymentId, paymentStatus, email, name, phone} = booking;
	return (
		<>
			{[
				{
					label: t("booking.site"),
					value: (
						<>
							<Typography>
								<strong>{site.name}</strong>
							</Typography>
							<Typography variant="body2">{site.street}</Typography>
							<Typography variant="body2">{site.city} {site.country}</Typography>
							{notBlank(site.accessDescription) && (
								<Alert severity="info" sx={{mt: 2}}>{site.accessDescription}</Alert>
							)}
						</>
					),
				},
				{
					label: t("sites.accessibility"),
					value: <Accessibility site={site} />,
				},
				...(notBlank(paymentId)
					? [{
						label: t("payment.status"),
						value: <span>{paymentStatus?.toUpperCase()}</span>,
					}]
					: []),
				...([
					{
						label: t("customer.email"),
						value: email,
					},
					{
						label: t("customer.name"),
						value: name,
					},
					{
						label: t("customer.phone"),
						value: phone,
					},
				]
					.filter(({value}) => notBlank(value))),
			]
				.map(({label, value}) => (
					<TableRow key={label}>
						<TableCell
							component="th"
							scope="row"
						>
							{label}:
						</TableCell>
						<TableCell align="right">{value}</TableCell>
					</TableRow>
				))}
		</>
	);
}

function BookingPaymentList({payments, zoneId, currency}: {
	payments: Array<B2BookingPayment>;
	zoneId: string;
	currency: string | undefined;
}) {
	const {t} = useLocalization();

	const [receipt, setReceipt] = useState<string>();

	return (
		<>
			<List dense disablePadding>
				{payments
					.sort((p1, p2) =>
						ZonedDateTime.compare(
							parseZoneDateTimeFromInstant(p1.createdAt, zoneId),
							parseZoneDateTimeFromInstant(p2.createdAt, zoneId),
						)
					)
					.map(({uuid, paidAt, paymentId, amount, createdAt, receiptText}) => (
						<ListItem key={uuid} style={{padding: 0}}>
							<ListItemAvatar>
								<Avatar
									sx={theme => ({
										background: notBlank(paidAt) ? green[500] : theme.palette.grey[200],
										color: notBlank(paidAt) ? "#fff" : theme.palette.grey[500],
									})}
								>
									{paymentId ? <CreditCardIcon /> : <ReceiptIcon />}
								</Avatar>
							</ListItemAvatar>
							<ListItemText
								primary={
									<span>
										{currency ?? "EUR"} <DisplayCurrency amount={amount} />
									</span>
								}
								secondary={<TemporalFormat value={parseZoneDateTimeFromInstant(createdAt, zoneId)} />}
							/>
							{/* TODO: Sloppily detecting actual receipt with min length until we have a proper flag for this data. */}
							{(receiptText ?? "").length > 200 && (
								<ListItemSecondaryAction>
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={() => setReceipt(receiptText)}
									>
										{t("payment.receipt")}
									</Button>
								</ListItemSecondaryAction>
							)}
						</ListItem>
					))}
			</List>
			<Dialog
				open={notBlank(receipt)}
				onClose={() => setReceipt(undefined)}
			>
				<DialogTitle>{t("payment.receipt")}</DialogTitle>
				<DialogContent>
					<pre style={{fontSize: "0.7rem"}}>{receipt}</pre>
					<Box mt={2} mb={1} display="flex" justifyContent="center">
						<Button
							variant="contained"
							onClick={() => setReceipt(undefined)}
						>
							{t("actions.close")}
						</Button>
					</Box>
				</DialogContent>
			</Dialog>
		</>
	);
}
